import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Send, TriangleAlert } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { useToast } from '../../components/ErrorToast';
import { Button, Callout, textareaCls } from '../../components/ui';
import { api, rulesKeys } from '../../lib/api';
import { isTransient } from '../../lib/errors';
import { useRole } from '../../lib/role';
import { LABEL, T } from './texts';

/*
 * «Нет нужного правила?» — просьба о правиле (004, US6, T100; FR-057; сценарий US6 5): текст словами и «Отправить
 * команде разработки». Перед отправкой сервер проверяет текст на ФИО и номера людей объекта (`POST …/requests/check`): это
 * только предупреждение «В просьбе есть ФИО или номер. Команда разработки увидит этот текст» — второе нажатие отправляет текст
 * как есть (решение 15.10.2026: внутренний контур). Просьба получает статус «Ждёт разбора». Окно каталога и вид
 * «Просьбы о правилах» (администратор правил и утверждающий).
 */

const TEXT_MAX = 2000;

export function DescribeRule({
  onSent,
  onDirty,
}: {
  /** Просьба сохранена (окно каталога закрывается). */
  onSent?: () => void;
  /** Введён текст: окно сначала спрашивает, закрывать ли его. */
  onDirty?: (dirty: boolean) => void;
}) {
  const ids = useId();
  const { role } = useRole();
  const qc = useQueryClient();
  const { showError, showInfo } = useToast();
  const [text, setText] = useState('');
  /** Текст, на который сервер предупредил о ФИО: тот же текст второе нажатие отправляет. */
  const [warnedFor, setWarnedFor] = useState<string | null>(null);
  const t = text.trim();
  useEffect(() => onDirty?.(t !== ''), [t, onDirty]);

  const send = useMutation({
    mutationFn: async (): Promise<'warned' | 'sent'> => {
      if (warnedFor !== t) {
        const check = await api.checkRuleRequest(t);
        if (check.warnings.includes('personal_data')) return 'warned';
      }
      await api.createRuleRequest(t);
      return 'sent';
    },
    onSuccess: (result) => {
      if (result === 'warned') {
        setWarnedFor(t);
        return;
      }
      showInfo(T.sent, { duration: 6000 });
      void qc.invalidateQueries({ queryKey: rulesKeys.requests(role) });
      setText('');
      setWarnedFor(null);
      onDirty?.(false);
      onSent?.();
    },
    onError: (e) =>
      showError(e, {
        screen: 'Rules',
        action: 'rule request',
        retry: isTransient(e) ? () => send.mutate() : undefined,
      }),
  });

  const warned = warnedFor !== null && warnedFor === t;
  const reason = t === '' ? T.whyDescribe : null;

  return (
    <section aria-label={LABEL.noRule} className="space-y-2">
      <h3 className="text-[14px] font-semibold text-ink">{LABEL.noRule}</h3>
      <div className="space-y-1.5">
        <label htmlFor={`${ids}-text`} className="block text-[13.5px] font-medium text-ink">
          {LABEL.describe}
        </label>
        <textarea
          id={`${ids}-text`}
          rows={3}
          maxLength={TEXT_MAX}
          value={text}
          placeholder={T.describePlaceholder}
          aria-describedby={`${ids}-hint`}
          onChange={(e) => setText(e.target.value)}
          className={textareaCls}
        />
        <p id={`${ids}-hint`} className="text-[12px] text-ink-3">
          {T.describeHint}
        </p>
      </div>
      {warned && (
        <Callout tone="warn" icon={TriangleAlert}>
          <p>{T.describePersonal}</p>
          <p className="text-ink-2">{T.describeAgain}</p>
        </Callout>
      )}
      <div className="flex flex-wrap items-center justify-end gap-2 max-sm:[&>button]:flex-auto">
        {reason && (
          <p id={`${ids}-why`} className="mr-auto text-[12.5px] text-ink-2 max-sm:w-full">
            {reason}
          </p>
        )}
        <Button
          icon={Send}
          disabled={reason !== null || send.isPending}
          aria-describedby={reason ? `${ids}-why` : undefined}
          onClick={() => send.mutate()}
        >
          {LABEL.send}
        </Button>
      </div>
    </section>
  );
}
